import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { S, t, tList, type Locale } from '../../i18n/strings';
import { pick } from '../../lib/random';
import {
  BANNER_BADGE_WIDTH,
  BANNER_FILENAME,
  BANNER_HEIGHT,
  BANNER_MAX_LENGTH,
  BANNER_SCHEMES,
  BANNER_SPEEDS,
  BANNER_WIDTH,
  bannerPalette,
  bannerSnippet,
  findScheme,
  findSpeed,
  quantise,
  sanitizeBannerText,
  splitBannerLines,
  type BannerScheme,
} from './banner';
import { encodeGif } from './gif';
import styles from './y2k.module.css';

/** Corps de départ du texte, et le plus petit auquel on accepte de descendre. */
const FONT_MAX = 13;
const FONT_MIN = 7;
/** Une disquette 3"½ haute densité, en octets. L'unité de mesure de 1998. */
const FLOPPY_BYTES = 1_474_560;

/**
 * Dessine une image de la bannière dans un contexte de 88 × 31.
 *
 * La vignette de gauche est toujours noire et le poisson toujours orange, quel
 * que soit le thème : c'est la seule contrainte non négociable du dessin, et
 * elle a l'avantage de garantir aussi qu'il reste lisible sur les cinq fonds.
 */
function paintFrame(
  context: CanvasRenderingContext2D,
  scheme: BannerScheme,
  lines: readonly string[],
  blink: boolean,
): void {
  const textLeft = BANNER_BADGE_WIDTH + 1;
  const textWidth = BANNER_WIDTH - textLeft - 1;

  context.fillStyle = scheme.edge;
  context.fillRect(0, 0, BANNER_WIDTH, BANNER_HEIGHT);
  context.fillStyle = '#000000';
  context.fillRect(1, 1, BANNER_BADGE_WIDTH, BANNER_HEIGHT - 2);
  context.fillStyle = scheme.bg;
  context.fillRect(textLeft, 1, textWidth, BANNER_HEIGHT - 2);

  // ── Steve, en vingt-quatre pixels de large ───────────────────────────────
  // Queue d'abord : bord plat à gauche, pointe vers le corps, comme partout.
  context.fillStyle = '#c2410c';
  context.beginPath();
  context.moveTo(3, 10);
  context.lineTo(3, 22);
  context.lineTo(10, 16);
  context.closePath();
  context.fill();
  // Jambes et bras, avant le corps : seuls leurs bouts dépassent.
  context.fillRect(11, 21, 2, 5);
  context.fillRect(16, 21, 2, 5);
  context.fillRect(17, 18, 5, 2);

  context.fillStyle = '#f97316';
  context.beginPath();
  context.ellipse(15, 16, 8, 6, 0, 0, Math.PI * 2);
  context.fill();

  context.fillStyle = '#ffffff';
  context.beginPath();
  context.ellipse(19, 13, 3, 3, 0, 0, Math.PI * 2);
  context.fill();
  context.fillStyle = '#000000';
  context.beginPath();
  context.ellipse(19, 13, 1.4, 1.4, 0, 0, Math.PI * 2);
  context.fill();

  // ── Le texte ─────────────────────────────────────────────────────────────
  if (lines.length === 0) return;
  context.fillStyle = blink ? scheme.accent : scheme.ink;
  context.textAlign = 'center';
  context.textBaseline = 'middle';

  const centre = textLeft + textWidth / 2;
  const rows = lines.length === 1 ? [16] : [11, 22];
  for (const [index, line] of lines.entries()) {
    /*
     * Le corps est ajusté à la mesure, pas au nombre de caractères : « MMM » et
     * « iii » n'occupent pas la même place, et une bannière qui déborde de deux
     * pixels ne se rattrape pas après coup.
     */
    let size = FONT_MAX;
    context.font = `${String(size)}px "VT323", monospace`;
    while (size > FONT_MIN && context.measureText(line).width > textWidth - 4) {
      size -= 1;
      context.font = `${String(size)}px "VT323", monospace`;
    }
    context.fillText(line, centre, rows[index] ?? 16);
  }
}

/** Le fichier complet, à partir du texte et du thème. */
function buildGif(
  canvas: HTMLCanvasElement,
  scheme: BannerScheme,
  lines: readonly string[],
  delayMs: number,
): Uint8Array<ArrayBuffer> | null {
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return null;
  const palette = bannerPalette(scheme);

  const frames = [false, true].map((blink) => {
    context.clearRect(0, 0, BANNER_WIDTH, BANNER_HEIGHT);
    paintFrame(context, scheme, lines, blink);
    const pixels = context.getImageData(0, 0, BANNER_WIDTH, BANNER_HEIGHT).data;
    return { indices: quantise(pixels, palette), delayMs };
  });

  return encodeGif({ width: BANNER_WIDTH, height: BANNER_HEIGHT, palette, frames });
}

interface Props {
  readonly lang: Locale;
}

/**
 * La fabrique de bannières 88 × 31.
 *
 * L'aperçu **est** le fichier : le GIF est reconstruit à chaque changement et
 * affiché tel quel dans une balise `img`. Rien ne peut donc diverger entre ce
 * qu'on voit et ce qu'on télécharge — un aperçu simulé en CSS aurait fini par
 * mentir sur une couleur ou sur la vitesse du clignotement.
 *
 * Le poids est celui du vrai fichier, et il est affiché : trois kilo-octets,
 * c'était une seconde de modem.
 */
export function BannerFactory({ lang }: Props): ReactNode {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const snippetRef = useRef<HTMLTextAreaElement>(null);
  const slogans = tList(S.y2k.banner.slogans, lang);

  const [raw, setRaw] = useState(() => slogans[0] ?? 'LE POISSON STEVE');
  const [schemeId, setSchemeId] = useState('nuit');
  const [speedId, setSpeedId] = useState('normal');
  const [url, setUrl] = useState('');
  const [weight, setWeight] = useState(0);
  const [copied, setCopied] = useState(false);
  /*
   * `VT323` n'est pas forcément arrivée quand le premier dessin a lieu, et un
   * canvas ne redessine pas tout seul à l'arrivée d'une police : sans ce
   * drapeau, la première bannière sortait en police de repli, définitivement.
   */
  const [fontReady, setFontReady] = useState(false);

  const text = sanitizeBannerText(raw);
  const lines = useMemo(() => splitBannerLines(text), [text]);
  const scheme = findScheme(schemeId);
  const speed = findSpeed(speedId);
  const href = typeof window === 'undefined' ? '/' : `${window.location.origin}/`;
  const snippet = bannerSnippet(text, href);

  useEffect(() => {
    let alive = true;
    /*
     * `document.fonts` n'existe pas partout — l'environnement des tests
     * unitaires, notamment, n'a ni polices ni canvas. On ne peut pas y attendre
     * une promesse qui n'existe pas, et surtout on ne doit pas y laisser une
     * exception faire tomber tout le skin.
     */
    const fonts = document.fonts as FontFaceSet | undefined;
    if (!fonts) {
      setFontReady(true);
      return;
    }
    fonts
      .load('13px "VT323"')
      .catch(() => undefined)
      .finally(() => {
        if (alive) setFontReady(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !fontReady) return;
    const bytes = buildGif(canvas, scheme, lines, speed.delayMs);
    if (!bytes) return;

    const blob = new Blob([bytes], { type: 'image/gif' });
    const next = URL.createObjectURL(blob);
    setUrl(next);
    setWeight(bytes.length);
    return () => {
      // Un objet non révoqué garde son blob en mémoire jusqu'au rechargement,
      // et il s'en crée un à chaque frappe.
      URL.revokeObjectURL(next);
    };
  }, [scheme, lines, speed.delayMs, fontReady]);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => {
      setCopied(false);
    }, 1600);
    return () => {
      window.clearTimeout(timer);
    };
  }, [copied]);

  const floppies = weight > 0 ? Math.floor(FLOPPY_BYTES / weight) : 0;

  return (
    <div className={styles.banner}>
      <p className={styles.bannerIntro}>{t(S.y2k.banner.intro, lang)}</p>

      {/* Hors écran : c'est l'atelier, pas l'aperçu. */}
      <canvas ref={canvasRef} width={BANNER_WIDTH} height={BANNER_HEIGHT} className={styles.bannerCanvas} />

      <div className={styles.bannerStage}>
        {url === '' ? null : (
          <img
            className={styles.bannerPreview}
            src={url}
            width={BANNER_WIDTH}
            height={BANNER_HEIGHT}
            alt={t(S.y2k.banner.preview, lang)}
          />
        )}
      </div>

      <div className={styles.bannerRow}>
        <label className={styles.bannerLabel} htmlFor="y2k-banner-text">
          {t(S.y2k.banner.text_label, lang)}
        </label>
        <input
          id="y2k-banner-text"
          type="text"
          className={styles.bannerInput}
          value={raw}
          maxLength={BANNER_MAX_LENGTH * 2}
          onChange={(event) => {
            setRaw(event.currentTarget.value);
          }}
        />
        <button
          type="button"
          className={styles.bannerButton}
          onClick={() => {
            const others = slogans.filter((slogan) => slogan !== text);
            setRaw(pick(others, slogans[0] ?? ''));
          }}
        >
          {t(S.y2k.banner.shuffle, lang)}
        </button>
      </div>

      <div className={styles.bannerRow}>
        <span className={styles.bannerLabel}>{t(S.y2k.banner.scheme_label, lang)}</span>
        <div className={styles.bannerChoices} role="group" aria-label={t(S.y2k.banner.scheme_label, lang)}>
          {BANNER_SCHEMES.map((option) => (
            <button
              key={option.id}
              type="button"
              className={styles.bannerSwatch}
              aria-pressed={option.id === schemeId}
              style={{ background: option.bg, color: option.ink, borderColor: option.edge }}
              onClick={() => {
                setSchemeId(option.id);
              }}
            >
              {t(option.label, lang)}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.bannerRow}>
        <span className={styles.bannerLabel}>{t(S.y2k.banner.speed_label, lang)}</span>
        <div className={styles.bannerChoices} role="group" aria-label={t(S.y2k.banner.speed_label, lang)}>
          {BANNER_SPEEDS.map((option) => (
            <button
              key={option.id}
              type="button"
              className={styles.bannerButton}
              aria-pressed={option.id === speedId}
              onClick={() => {
                setSpeedId(option.id);
              }}
            >
              {t(option.label, lang)}
            </button>
          ))}
        </div>
      </div>

      {/*
        Rien tant que le fichier n'existe pas.
        Le premier dessin attend l'arrivée de la police, soit quelques images.
        Afficher d'ici là un lien au `href` vide — qui pointe sur la page
        courante — et un poids de zéro octet aurait été le genre de faux bouton
        qu'on s'interdit partout ailleurs.
      */}
      {url === '' ? null : (
        <div className={styles.bannerRow}>
          <a className={styles.bannerDownload} href={url} download={BANNER_FILENAME}>
            {t(S.y2k.banner.download, lang)}
          </a>
          <span className={styles.bannerWeight}>
            {t(S.y2k.banner.weight, lang, { n: weight, m: floppies })}
          </span>
        </div>
      )}

      <label className={styles.bannerLabel} htmlFor="y2k-banner-code">
        {t(S.y2k.banner.snippet_label, lang)}
      </label>
      <div className={styles.bannerRow}>
        <textarea
          ref={snippetRef}
          id="y2k-banner-code"
          className={styles.bannerCode}
          readOnly
          rows={3}
          value={snippet}
          onFocus={(event) => {
            event.currentTarget.select();
          }}
        />
        <button
          type="button"
          className={styles.bannerButton}
          onClick={() => {
            /*
             * Le presse-papier peut être refusé (contexte non sécurisé, réglage
             * du navigateur). Dans ce cas on sélectionne le texte : le visiteur
             * fait `Ctrl+C` lui-même, ce qui est de toute façon le geste de
             * 1998.
             */
            navigator.clipboard?.writeText(snippet).then(
              () => {
                setCopied(true);
              },
              () => {
                snippetRef.current?.select();
              },
            );
          }}
        >
          {copied ? t(S.y2k.banner.copied, lang) : t(S.y2k.banner.copy, lang)}
        </button>
      </div>
    </div>
  );
}
